class CCMenu extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const signal = this.controller.signal;
    this.buttons = [...this.querySelectorAll('[aria-controls]')];
    for (const button of this.buttons) {
      const item = button.parentElement;
      button.addEventListener('pointerdown', (event) => { this.pointer = event.pointerType; }, { signal });
      button.addEventListener('click', (event) => {
        const touch = this.pointer === 'touch' || this.pointer === 'pen';
        this.pointer = null;
        if (touch && button.getAttribute('aria-expanded') !== 'true') {
          event.preventDefault();
          this.open(button);
        }
      }, { signal });
      button.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowDown' && event.key !== ' ') return;
        event.preventDefault();
        this.open(button);
        this.panel(button).querySelector('a')?.focus();
      }, { signal });
      item.addEventListener('pointerenter', (event) => {
        if (event.pointerType !== 'mouse') return;
        clearTimeout(this.timer);
        this.open(button);
      }, { signal });
      item.addEventListener('pointerleave', (event) => {
        if (event.pointerType !== 'mouse') return;
        clearTimeout(this.timer);
        this.timer = setTimeout(() => this.close(button), 200);
      }, { signal });
    }
    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || !this.current) return;
      const button = this.current;
      this.close(button);
      button.focus();
    }, { signal });
    document.addEventListener('click', (event) => {
      if (this.current && !this.contains(event.target)) this.close(this.current);
    }, { signal });
    this.addEventListener('focusout', (event) => {
      if (this.current && !this.current.parentElement.contains(event.relatedTarget)) this.close(this.current);
    }, { signal });
    this.header = this.closest('[data-header]');
    this.list = this.querySelector('[data-menu-list]');
    this.logo = this.header?.querySelector('[data-header-logo]');
    this.desktop = matchMedia('(min-width: 990px)');
    window.addEventListener('resize', () => this.fit(), { signal });
    document.fonts?.ready.then(() => this.fit());
    this.fit();
  }

  fit() {
    if (!this.header || !this.list || !this.logo) return;
    this.header.removeAttribute('data-compact');
    if (!this.desktop.matches) return;
    const end = this.list.getBoundingClientRect().left + this.list.scrollWidth;
    this.header.toggleAttribute('data-compact', end > this.logo.getBoundingClientRect().left);
  }

  disconnectedCallback() {
    this.controller.abort();
    clearTimeout(this.timer);
  }

  panel(button) {
    return document.getElementById(button.getAttribute('aria-controls'));
  }

  open(button) {
    if (this.current && this.current !== button) this.close(this.current);
    button.setAttribute('aria-expanded', 'true');
    this.panel(button).hidden = false;
    this.current = button;
    const header = this.closest('[data-header]');
    header?.style.setProperty('--scrim-top', `${header.getBoundingClientRect().bottom}px`);
    header?.setAttribute('data-open', '');
  }

  close(button) {
    button.setAttribute('aria-expanded', 'false');
    this.panel(button).hidden = true;
    if (this.current === button) this.current = null;
    if (!this.current) this.closest('[data-header]')?.removeAttribute('data-open');
  }

}

class CCDrawer extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.controller = new AbortController();
    const signal = this.controller.signal;
    document.addEventListener('click', (event) => {
      const trigger = event.target.closest(`[data-drawer-open="${this.id}"]`);
      if (!trigger) return;
      event.preventDefault();
      this.trigger = trigger;
      this.open();
    }, { signal });
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog || event.target.closest('[data-drawer-close]')) this.close();
    }, { signal });
    this.dialog.addEventListener('close', () => {
      this.trigger?.setAttribute('aria-expanded', 'false');
      this.trigger?.focus();
    }, { signal });
  }

  disconnectedCallback() {
    this.controller.abort();
  }

  open() {
    this.dialog.showModal();
    this.trigger?.setAttribute('aria-expanded', 'true');
  }

  close() {
    this.dialog.close();
  }
}

class CCAccordion extends HTMLElement {
  connectedCallback() {
    this.query = matchMedia('(min-width: 750px)');
    this.sync = () => {
      for (const details of this.querySelectorAll('details')) {
        details.open = this.query.matches;
        details.querySelector('summary')?.setAttribute('tabindex', this.query.matches ? '-1' : '0');
      }
    };
    this.sync();
    this.query.addEventListener('change', this.sync);
  }

  disconnectedCallback() {
    this.query.removeEventListener('change', this.sync);
  }
}

if (!customElements.get('cc-menu')) customElements.define('cc-menu', CCMenu);
if (!customElements.get('cc-drawer')) customElements.define('cc-drawer', CCDrawer);
if (!customElements.get('cc-accordion')) customElements.define('cc-accordion', CCAccordion);
